import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Grid, GridColumn, GridNoRecords } from "@progress/kendo-react-grid";
import { PanelBar, PanelBarItem } from "@progress/kendo-react-layout";
import {
  setFavouriteFilters,
  setLibraryBookmarks,
  setProjectBookmarks,
  setQueueBookmarks,
  setTaskBookmarks,
} from "../../actions/bookmarks";
import { setTeamSavedFilter } from "../../actions/projects";
import { label, number, route } from "../../config";
import { addUpdateFavouriteFilters, saveFavouriteFilterOrder } from "../../shared/services/projects.service";
import { updateContentAttachment, updateFavoriteSortOrder } from "../../shared/services/contentManger.services";
import { joinString } from "../../utils";
import { GridContext, reordering } from "../../utils/kendo";
import MarkBookmark from "./MarkBookmark";
import MarkFavouriteFilter from "./MarkFavouriteFilter";
import "./bookmarks.scss";
import { changeBookmarkOrder, updateBookmark } from "./bookmarks.service";

const BookmarksList = ({ type, setType }) => {
  const { taskBookmarks, projectBookmarks, queueBookmarks, libraryBookmarks, favouriteFilters } = useSelector((state) => state.bookmarks);
  const teamSavedFilters = useSelector((state) => state.teamFilter.teamSavedFilters);

  const gridDataMap = useMemo(
    () => ({
      [label.FAVOURITE_FILTERS_GRID_TYPE]: favouriteFilters || [],
      [route.PRIVATE_ROUTE.TASKS.ENTITY_NAME]: taskBookmarks,
      [route.PRIVATE_ROUTE.PROJECT.ENTITY_NAME]: projectBookmarks,
      [route.PRIVATE_ROUTE.CMS.ENTITY_NAME]: libraryBookmarks,
    }),
    [favouriteFilters, taskBookmarks, projectBookmarks, libraryBookmarks]
  );

  const getSourceGridData = (bookmarkType) => gridDataMap[bookmarkType] ?? queueBookmarks ?? [];

  const [gridData, setGridData] = useState(() => gridDataMap[type] ?? queueBookmarks ?? []);
  const [activeItem, setActiveItem] = useState(null);
  const { id } = useSelector((state) => state.auth.user);
  const userId = id;
  const dispatch = useDispatch();

  useEffect(() => {
    setGridData(getSourceGridData(type));
  }, [type, gridDataMap, queueBookmarks]);

  /**
   * to get the order of bookmarks
   * @param {*} reorderedData
   * @returns order of bookmarks and its type
   */
  const getEntities = (reorderedData) => {
    let entity = {
      entityType: "",
      entityId: [],
    };
    reorderedData.map((data) => {
      entity.entityId.push(data.Id);
      if (data.EntityType === route.PRIVATE_ROUTE.TASKS.ENTITY_NAME) return (entity.entityType = number.FOUR);
      if (data.EntityType === route.PRIVATE_ROUTE.PROJECT.ENTITY_NAME) return (entity.entityType = number.THREE);
      if (data.EntityType === route.PRIVATE_ROUTE.CMS.ENTITY_NAME) return (entity.entityType = number.FIVE);
      return (entity.entityType = number.SEVEN);
    });
    return entity;
  };

  /**
   * Dispatches bookmark data to the corresponding Redux action based on entity type.
   * Defaults to queue bookmarks if no match is found.
   *
   * @param {Object} dispatchData - The data to be dispatched to the Redux store.
   * @author Dimple Sahota
   */
  const dispatchBookmarkType = (dispatchData) => {
    if (type === label.FAVOURITE_FILTERS_GRID_TYPE) {
      dispatch(setFavouriteFilters(dispatchData));
      return;
    }
    const typeToActionMap = {
      [route.PRIVATE_ROUTE.TASKS?.ENTITY_NAME]: setTaskBookmarks,
      [route.PRIVATE_ROUTE.PROJECT?.ENTITY_NAME]: setProjectBookmarks,
      [route.PRIVATE_ROUTE.CMS?.ENTITY_NAME]: setLibraryBookmarks,
    };

    const action = typeToActionMap[type] || setQueueBookmarks;
    dispatch(action(dispatchData));
  };

  /**
   * Reorders the bookmarks in the grid data.
   * @param {Object} dataItem - The item being reordered.
   * @author Dimple Sahota
   */

  const reorder = (dataItem) => {
    let reorderedData = reordering(dataItem, activeItem, gridData);
    if (reorderedData) {
      setGridData(reorderedData);
      // Dispatch appropriate action based on the bookmark type
      dispatchBookmarkType(reorderedData);
    }
  };

  /**
   * Is called automatically when the bookmark component is dropped
   * Handles the drop event when a bookmark is dropped after dragging.
   * Updates the bookmark order.
   * @param {DragEvent} e - The drag event.
   * @author Dimple Sahota
   */
  const onDrop = async (e) => {
    if (type === label.FAVOURITE_FILTERS_GRID_TYPE) {
      const filterIds = (gridData || []).map((f) => f.FilterId).join(",");
      if (filterIds) {
        await saveFavouriteFilterOrder({ filterIds });
      }
      return;
    }

    const entity = getEntities(gridData);

    if (!entity) return;

    const isLibrary = entity.entityType === number.FIVE;

    if (isLibrary) {
      const orderedAttachmentIds = (gridData || []).map((item, index) => ({
        Id: item.Id,
        SortId: index + 1,
      }));

      const payload = { userId, orderedAttachmentIds };
      await updateFavoriteSortOrder(payload);
    } else {
      const payload = {
        userId,
        order: joinString(entity.entityId, ","),
        entityType: entity.entityType,
      };

      dispatch(changeBookmarkOrder(payload));
    }
  };

  /**
   * Handles the drag start event for a bookmark.
   * @param {Object} dataItem - The item being dragged.
   */
  const dragStart = (dataItem) => {
    setActiveItem(dataItem);
  };

  /**
   * Removes a bookmark from favorites.
   * @param {Object} dataItem - The bookmark item to be removed.
   * @author Dimple Sahota
   */
  const handleRemoveFavorite = (dataItem) => {
    const entityType =
      dataItem.EntityType === route.PRIVATE_ROUTE.TASKS?.ENTITY_NAME
        ? route.PRIVATE_ROUTE.TASKS?.ENTITY_NAME
        : dataItem.EntityType === route.PRIVATE_ROUTE.PROJECT?.ENTITY_NAME
        ? route.PRIVATE_ROUTE.PROJECT?.ENTITY_NAME
        : dataItem.EntityType === route.PRIVATE_ROUTE.CMS?.ENTITY_NAME
        ? route.PRIVATE_ROUTE.CMS?.ENTITY_NAME
        : route.PRIVATE_ROUTE.QUEUES?.ENTITY_NAME;

    const isLibrary = dataItem.EntityType === "Library";
    const itemId = dataItem?.Id;
    const filteredData = gridData?.filter((data) => data?.Id !== itemId);

    if (isLibrary) {
      updateContentAttachment({
        attachmentId: itemId,
        isFavorite: !dataItem?.isFavorite,
        projectId: dataItem?.ProjectId ?? null,
      });
    } else {
      dispatch(
        updateBookmark({
          userId,
          entityType,
          entityId: itemId,
          sortId: dataItem?.SortId,
          isBookmarkList: true,
        })
      );
    }
    dispatchBookmarkType(filteredData);

    setGridData(filteredData);
    setActiveItem(null);
  };

  const applyFavouriteFilterRemoval = (filteredData, filterId) => {
    setGridData(filteredData);
    dispatch(setFavouriteFilters(filteredData));
    dispatch(setTeamSavedFilter(mapTeamSavedFilterFavouriteFlag(teamSavedFilters, filterId, number.ZERO)));
  };
 
  const excludeFavouriteFilterById = (filters, filterId) => (filters || []).filter((f) => f.FilterId !== filterId);

   const mapTeamSavedFilterFavouriteFlag = (teamSavedFilters, filterId, isFavouriteFlag) =>
  (teamSavedFilters || []).map((f) => (f.value === filterId ? { ...f, IsFavouriteFilter: isFavouriteFlag } : f));

  const revertFavouriteFilterRemoval = (previousGridData, filterId) => {
    setGridData(previousGridData);
    dispatch(setFavouriteFilters(previousGridData));
    dispatch(setTeamSavedFilter(mapTeamSavedFilterFavouriteFlag(teamSavedFilters, filterId, number.ONE)));
  };

  const removeFavouriteFilterFromServer = (filterId) => addUpdateFavouriteFilters({ filterId });

  const handleRemoveFavouriteFilter = async (dataItem) => {
    const previousGridData = gridData;
    const filterId = dataItem.FilterId;
    const filteredData = excludeFavouriteFilterById(previousGridData, filterId);
    applyFavouriteFilterRemoval(filteredData, filterId);
    const response = await removeFavouriteFilterFromServer(filterId);
    if (!response) {
      revertFavouriteFilterRemoval(previousGridData, filterId);
    }
   setActiveItem(null);
  };

  const isFavouriteFiltersGrid = type === label.FAVOURITE_FILTERS_GRID_TYPE;

  return (
    <div className='bookmarks-list-container'>
      <PanelBar>
        <PanelBarItem expanded={true}>
          <GridContext.Provider
            value={{
              reorder: reorder,
              dragStart: dragStart,
              onDrop: (e) => onDrop(e),
              activeItem,
              setActiveItem,
            }}>
            <Grid data={gridData} data-boxtype='red' className={isFavouriteFiltersGrid && "favourite-filters-bookmarks-grid"}>
              <GridNoRecords>
                <span>{label.NOTHING_BOOKMARKED}</span>
              </GridNoRecords>
              <GridColumn
                cell={(props) =>
                  isFavouriteFiltersGrid ? (
                    <MarkFavouriteFilter {...props} handleRemoveFavorite={handleRemoveFavouriteFilter} />
                  ) : (
                    <MarkBookmark {...props} type={type} handleRemoveFavorite={handleRemoveFavorite} setType={setType} />
                  )
                }
              />
            </Grid>
          </GridContext.Provider>
        </PanelBarItem>
      </PanelBar>
    </div>
  );
};

export default BookmarksList;
